import type { Graph } from "@hydrologiq/schemas/1.0.0/installationPage"

export type TimescaleGraphTemplate = Partial<Graph> & {
  aggregation?: string
  aggregationRole?: string
  filter?: { role?: string; class?: string }
  dualMetric?: boolean
  breakdownToggle?: boolean
  defaultBreakdown?: boolean
  showPeak?: boolean
  aggregatedValueLabel?: string
  useStackedBarChart?: boolean
  /** Render the per-category breakdown as grouped (side-by-side) bars. */
  useGroupedBarChart?: boolean
  availableOverlays?: Array<{
    graphId: string
    label: string
    yAxisLabel: string
    defaultEnabled?: boolean
    useSameYAxis?: boolean
  }>
  stackedBarPeaksDefaultOff?: boolean
  stackedBarUseConsumerMenu?: boolean
  batteryView?: boolean
  /**
   * Eligible for one-asset-at-a-time viewing (dropdown, no aggregate). Only takes
   * effect where the host opts in with `selectOneAsset` (the systems view).
   */
  singleAssetSelect?: boolean
  /**
   * Render as side-by-side bars (one per asset, with the multi-select menu) rather
   * than stacked, so assets are never visually summed. Only takes effect where the
   * host opts in with `perAssetView` (the systems view).
   */
  groupedInSystemView?: boolean
  includeConnections?: boolean
  connectionsOnly?: boolean
}

export const FIXED_MONITORING_GRAPHS: TimescaleGraphTemplate[] = [
  {
    title: "Power Generation",
    yAxis: "Power (kW)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "power",
      sourceUnit: "W",
      desiredUnit: "kW",
      type: "TimescaleDB",
    },
    aggregation: "system",
    filter: { role: "generation" },
    breakdownToggle: false,
    defaultBreakdown: true,
    showPeak: true,
    stackedBarPeaksDefaultOff: true,
    aggregatedValueLabel: "Power generated",
    useStackedBarChart: true,
    // "Site Load" reference line overlaid on generation for at-a-glance
    // generation-vs-load comparison. Rendered as a SINGLE aggregated (summed)
    // line only — the overlay's per-asset breakdown is intentionally not drawn,
    // so multiple load-role contributors (VE.Bus output + AC-coupled PV
    // inverter) can't reproduce the duplicate "Site Load" lines seen before.
    availableOverlays: [{
      graphId: "power__aggregationRole_load",
      label: "Site Load",
      yAxisLabel: "Load (kW)",
      defaultEnabled: true,
      useSameYAxis: true,
    }],
    versioning: { schema: "1.0.0", revision: 4 },
  },
  {
    title: "Battery",
    yAxis: "Power (kW)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "power",
      sourceUnit: "W",
      desiredUnit: "kW",
      type: "TimescaleDB",
    },
    filter: { class: "battery" },
    defaultBreakdown: true,
    batteryView: true,
    singleAssetSelect: true,
    connectionsOnly: true,
    availableOverlays: [{
      graphId: "soc__class_battery",
      label: "State of Charge",
      yAxisLabel: "SoC (%)",
      defaultEnabled: true,
    }],
    versioning: { schema: "1.0.0", revision: 2 },
  },
  {
    title: "Load",
    yAxis: "Site Load (kW)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "power",
      sourceUnit: "W",
      desiredUnit: "kW",
      type: "TimescaleDB",
    },
    aggregation: "system",
    aggregationRole: "load",
    // Breakdown queries filter.role first (legacy "consumer"), then the backend
    // falls back to aggregationRole ("load") when that yields no rows. This
    // spans the in-progress consumer→load role migration: unmigrated systems
    // still carry role="consumer", migrated ones (incl. the VE.Bus output and
    // AC-coupled PV inverter) carry role="load". Do NOT hard-swap to "load"
    // only — it would drop unmigrated load assets from the breakdown.
    filter: { role: "consumer" },
    defaultBreakdown: true,
    useStackedBarChart: true,
    showPeak: true,
    stackedBarPeaksDefaultOff: true,
    stackedBarUseConsumerMenu: true,
    includeConnections: true,
    aggregatedValueLabel: "System load",
    versioning: { schema: "1.0.0", revision: 5 },
  },
  {
    title: "Fuel Level",
    yAxis: "Weight (kg)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "weight",
      sourceUnit: "kg",
      type: "TimescaleDB",
    },
    filter: { class: "fuel" },
    defaultBreakdown: true,
    dualMetric: true,
    groupedInSystemView: true,
    aggregatedValueLabel: "Level",
    useStackedBarChart: true,
    stackedBarPeaksDefaultOff: true,
    versioning: { schema: "1.0.0", revision: 3 },
  },
  {
    title: "Fuel Level",
    yAxis: "Level",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "level",
      // @ts-expect-error Graph source type omits m³; Timescale level is cubic metres
      sourceUnit: "m3",
      type: "TimescaleDB",
    },
    aggregation: "system",
    filter: { class: "fuel" },
    defaultBreakdown: true,
    groupedInSystemView: true,
    aggregatedValueLabel: "System fuel level",
    useStackedBarChart: true,
    stackedBarPeaksDefaultOff: true,
    versioning: { schema: "1.0.0", revision: 2 },
  },
  // --- Hybrid tank level (system scope, single card) ---
  // One graph whose per-category series (Diesel/Water/Waste) come from
  // `summary_dimension` via getSystemMonitoringGraphs -> _system_dimension_graphs.
  // The backend returns the categories as an `assetBreakdown` (one series each),
  // so this renders as a single card with a legend per category rather than
  // three separate cards. No class filter: hybrid tank children have no
  // system_node, so tank classes are absent from availableAssetClasses;
  // visibility is gated purely by timescaleFields ("tank_level" appears there
  // only when the system has any level dimension data).
  {
    title: "Tank Level",
    yAxis: "Level (%)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "tank_level",
      sourceUnit: "%",
      type: "TimescaleDB",
    },
    // Per-category breakdown series (not a system-summed aggregate). Rendered
    // as grouped (side-by-side) category bars — Diesel/Water/Waste sit next to
    // each other per bucket with a legend, never summed/stacked. Level is a %
    // per category, so a grouped category chart reads more clearly than lines.
    defaultBreakdown: true,
    useStackedBarChart: false,
    useGroupedBarChart: true,
    versioning: { schema: "1.0.0", revision: 3 },
  },
  // --- Fuel consumed (system scope, hybrid parent) ---
  // The same figure the report shows: litres drawn from the tanks per bucket. A
  // hybrid's tanks are rolled up onto the hybrid in `summary_dimension`, so no
  // class filter applies (its tanks are absent from availableAssetClasses).
  // Served by getSystemMonitoringGraphs / getAssetBatchMonitoringGraphs from
  // `_fuel_consumed_graphs`: one series per hybrid or standalone tank in a system
  // and per tank category in a hybrid, with the total as the aggregate. Negative
  // buckets are already settled, so the bars are always drawable. The card is left
  // out when there is no data.
  {
    title: "Fuel Consumed",
    yAxis: "Fuel consumed (L)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "fuel_consumed",
      sourceUnit: "L",
      type: "TimescaleDB",
    },
    aggregation: "system",
    defaultBreakdown: true,
    useStackedBarChart: true,
    stackedBarPeaksDefaultOff: true,
    aggregatedValueLabel: "Fuel consumed",
    versioning: { schema: "1.0.0", revision: 1 },
  },
  {
    title: "Pressure",
    yAxis: "Pressure (bar)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "pressure",
      type: "TimescaleDB",
    },
    filter: { class: "fuel" },
    defaultBreakdown: true,
    useStackedBarChart: false,
    versioning: { schema: "1.0.0", revision: 1 },
  },
  // --- Hydrogen Storage ---
  // Batch query integration: these templates are handled by the generic GraphRequest path
  // (not batteryView) which sets assetClass: "storage" from filter.class.
  // Generated graphIds: weight__class_storage, pressure__class_storage, temperature__class_storage
  {
    title: "Hydrogen Weight",
    yAxis: "Weight (kg)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "weight",
      sourceUnit: "kg",
      type: "TimescaleDB",
    },
    filter: { class: "storage" },
    groupedInSystemView: true,
    defaultBreakdown: true,
    useStackedBarChart: true,
    stackedBarPeaksDefaultOff: true,
    versioning: { schema: "1.0.0", revision: 1 },
  },
  {
    title: "Hydrogen Pressure",
    yAxis: "Pressure (bar)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "pressure",
      type: "TimescaleDB",
    },
    filter: { class: "storage" },
    defaultBreakdown: true,
    useStackedBarChart: false,
    versioning: { schema: "1.0.0", revision: 1 },
  },
  {
    title: "Hydrogen Temperature",
    yAxis: "Temperature (°C)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "temperature",
      type: "TimescaleDB",
    },
    filter: { class: "storage" },
    defaultBreakdown: true,
    useStackedBarChart: false,
    versioning: { schema: "1.0.0", revision: 1 },
  },
  // --- Electrolyser ---
  {
    title: "Flow Rate",
    yAxis: "Flow Rate (NL/hour)",
    size: "full",
    type: "TimeSeries",
    source: {
      field: "flow_rate",
      type: "TimescaleDB",
    },
    aggregation: "system",
    filter: { class: "electrolyser" },
    breakdownToggle: true,
    defaultBreakdown: false,
    useStackedBarChart: true,
    versioning: { schema: "1.0.0", revision: 3 },
  },
  {
    title: "Hydrogen Produced",
    yAxis: "Produced (NL)",
    size: "medium",
    type: "TimeSeries",
    source: {
      field: "produced",
      type: "TimescaleDB",
    },
    aggregation: "system",
    filter: { class: "electrolyser" },
    breakdownToggle: true,
    defaultBreakdown: false,
    useStackedBarChart: true,
    versioning: { schema: "1.0.0", revision: 2 },
  },
  {
    title: "Lifetime Production",
    yAxis: "Production (NL)",
    size: "medium",
    type: "TimeSeries",
    source: {
      field: "produced_total",
      type: "TimescaleDB",
    },
    aggregation: "system",
    filter: { class: "electrolyser" },
    breakdownToggle: true,
    defaultBreakdown: false,
    useStackedBarChart: true,
    versioning: { schema: "1.0.0", revision: 2 },
  },
]
